import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import Loading from 'components/Loading';
import { HelpOutlineIcon } from 'components/StyledIcons/StyledIcons';
import SurchargeWarning from 'components/Pricing/SurchargeWarning/Loadable';

const SectionSubHeader = styled(Typography)`
  && {
    font-weight: 400!important;
    color: #595959;
  }
`;

const GutterBottom = styled.div`
  width: 100%;
  margin-bottom: ${props => props.multiply ? `${props.multiply * 1.5}rem` : '1.5rem'};
`;

const PriceSummaryWrap = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-bottom: 1rem;
  padding: 1.5rem 1rem;
`;

const PriceItem = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PriceItemName = styled.div`
  display: flex;
  text-transform: capitalize;
`;

const PriceItemPrice = styled.div`
  display: flex;
`;

const PriceItemEm = styled(PriceItem)`
  font-weight: 700;
  font-size: 18px;
`;

const PriceItemEmGreen = styled(PriceItemEm)`
  color: #00822B;
`;

const SeparationLine = styled.div`
  display: block;
  height: 1px;
  width: 100%;
  background-color: rgb(219, 219, 219);
  margin: 0.75rem 0;
`;

const EditLink = styled.a`
  margin-left: 0.5rem;
  color: #00B33B;
  cursor: pointer;
`;

const PriceSummary = ({
  currency,
  totalPrice,
  payableNowPrice,
  currentSelectedCarType,
  bookingLanguage,
  carPriceFrom,
  languagePriceFrom,
  onScrollTo,
  loadingEnquiry,
  priceAdjustments,
  showAdjustments,
}) => {
  const { t } = useTranslation(['common']);
  const { name: carName = '' } = currentSelectedCarType || {};

  const {
    surcharges = [],
    discounts = [],
    surchargeAmount = 0,
    discountAmount = 0,
  } = priceAdjustments;

  const helpIconStyle = useMemo(() => ({
    root: {
      fontSize: '1rem',
      alignSelf: 'center',
      marginLeft: '0.25rem',
      color: '#595959',
      cursor: 'pointer'
    }
  }), []);

  return (
    <>
      {
        showAdjustments && (
          <SurchargeWarning
            surcharges={surcharges}
            discounts={discounts}
            currency={currency}
          />
        )
      }
      <PriceSummaryWrap>
        <SectionSubHeader variant="h4" component="h3">
          {`${t('PRICING.YOU_SELECTED')}:`}
          { loadingEnquiry ? <Loading /> : null }
        </SectionSubHeader>

        <GutterBottom />

        <PriceItem>
          <PriceItemName data-cy="cy-current-car-selection">
            {carName}
            <EditLink
              role="button"
              tabIndex={0}
              onClick={e => { onScrollTo(e, 'cartype'); }}
              onKeyDown={e => { onScrollTo(e, 'cartype'); }}
            >
              (edit)
            </EditLink>
          </PriceItemName>
          <PriceItemPrice>
            {`$${carPriceFrom} ${currency}`}
          </PriceItemPrice>
        </PriceItem>

        <PriceItem>
          <PriceItemName data-cy="cy-current-language-selection">
            {bookingLanguage}
            <EditLink
              role="button"
              tabIndex={0}
              onClick={e => { onScrollTo(e, 'languagetype'); }}
              onKeyDown={e => { onScrollTo(e, 'languagetype'); }}
            >
              (edit)
            </EditLink>
          </PriceItemName>
          <PriceItemPrice>
            {`$${languagePriceFrom} ${currency}`}
          </PriceItemPrice>
        </PriceItem>

        {
          surchargeAmount ? (
            <PriceItem>
              <PriceItemName data-cy="cy-adjustment-surcharge">
                Surcharge
                { showAdjustments ? null : (
                  <HelpOutlineIcon
                    overwritestyles={helpIconStyle}
                    onClick={e => { onScrollTo(e, 'paymentdetails'); }}
                    onKeyDown={e => { onScrollTo(e, 'paymentdetails'); }}
                  />
                )}
              </PriceItemName>
              <PriceItemPrice>
                {`$${surchargeAmount} ${currency}`}
              </PriceItemPrice>
            </PriceItem>
          ) : null
        }

        {
          discountAmount ? (
            <PriceItem>
              <PriceItemName data-cy="cy-adjustment-discount">
                Discount
                { showAdjustments ? null : (
                  <HelpOutlineIcon
                    overwritestyles={helpIconStyle}
                    onClick={e => { onScrollTo(e, 'paymentdetails'); }}
                    onKeyDown={e => { onScrollTo(e, 'paymentdetails'); }}
                  />
                )}
              </PriceItemName>
              <PriceItemPrice>
                {`-$${Math.abs(discountAmount)} ${currency}`}
              </PriceItemPrice>
            </PriceItem>
          ) : null
        }

        <SeparationLine />

        <PriceItem>
          <PriceItemName data-cy="cy-current-car-selection">
            {t('PRICING_PANEL.TOTAL')}
          </PriceItemName>
          <PriceItemPrice>
            {`$${totalPrice} ${currency}`}
          </PriceItemPrice>
        </PriceItem>

        <SeparationLine />

        <PriceItemEm>
          <PriceItemName data-cy="cy-current-car-selection">
            {t('PRICING_PANEL.PAYABLE_LATER')}
          </PriceItemName>
          <PriceItemPrice>
            {`$${totalPrice - payableNowPrice} ${currency}`}
          </PriceItemPrice>
        </PriceItemEm>

        <SeparationLine />

        <PriceItemEmGreen>
          <PriceItemName data-cy="cy-current-car-selection">
            {`${t('PRICING_PANEL.PAYABLE_NOW')} (${t('PRICING_PANEL.DEPOSIT')}):`}
          </PriceItemName>
          <PriceItemPrice>
            {`$${payableNowPrice} ${currency}`}
          </PriceItemPrice>
        </PriceItemEmGreen>

      </PriceSummaryWrap>
    </>
  );
};

PriceSummary.propTypes = {
  currency: PropTypes.string,
  totalPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  payableNowPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currentSelectedCarType: PropTypes.objectOf(PropTypes.any),
  priceAdjustments: PropTypes.objectOf(PropTypes.any),
  bookingLanguage: PropTypes.string,
  carPriceFrom: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  languagePriceFrom: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onScrollTo: PropTypes.func,
  loadingEnquiry: PropTypes.bool,
  showAdjustments: PropTypes.bool,
};

PriceSummary.defaultProps = {
  currency: '',
  totalPrice: 0,
  payableNowPrice: 0,
  currentSelectedCarType: {},
  bookingLanguage: '',
  carPriceFrom: 0,
  languagePriceFrom: 0,
  onScrollTo: () => {},
  loadingEnquiry: false,
  priceAdjustments: {},
  showAdjustments: true,
};

export default PriceSummary;
